import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import KitTile from '../components/KitTile';
import { STORAGE, DEFAULT_TABLE_SESSION } from '../constants/storageKeys';
import { usePersistentState } from '../hooks/usePersistentState';
import { copyText } from '../utils/share';
import { formatMsClock } from '../utils/time';
import { diceRollValues, diceLastTotal } from '../utils/diceSummary';
import { normalizePlayerSlots, TABLE_NAME_MAX, MAX_TABLE_PLAYERS } from '../utils/tableSession';
import './Hub.css';

function readJson(key, fallback) {
  try {
    const raw = localStorage.getItem(key);
    if (raw == null) return fallback;
    return JSON.parse(raw);
  } catch {
    return fallback;
  }
}

function timerPreview() {
  const t = readJson(STORAGE.timer, {});
  const now = Date.now();
  if (t.status === 'running' && t.endAt) return formatMsClock(Math.max(0, t.endAt - now));
  if (t.status === 'paused' && t.pausedRemainingMs != null) return formatMsClock(t.pausedRemainingMs);
  if (typeof t.durationMs === 'number') return formatMsClock(t.durationMs);
  return '—';
}

export default function Hub() {
  const [session, setSession] = usePersistentState(STORAGE.session, DEFAULT_TABLE_SESSION);
  const playerSlots = normalizePlayerSlots(session.players);

  useEffect(() => {
    const pull = () => {
      try {
        const raw = localStorage.getItem(STORAGE.session);
        const parsed = raw ? JSON.parse(raw) : null;
        const next = {
          gameName: typeof parsed?.gameName === 'string' ? parsed.gameName : '',
          players: normalizePlayerSlots(Array.isArray(parsed?.players) ? parsed.players : []),
        };
        setSession((prev) => {
          if (prev.gameName === next.gameName && JSON.stringify(prev.players) === JSON.stringify(next.players)) {
            return prev;
          }
          return next;
        });
      } catch {
        /* ignore */
      }
    };
    const onPersist = (e) => {
      if (e.detail?.key === STORAGE.session) pull();
    };
    const onStorage = (e) => {
      if (e.key === STORAGE.session) pull();
    };
    window.addEventListener('mt-persist', onPersist);
    window.addEventListener('storage', onStorage);
    return () => {
      window.removeEventListener('mt-persist', onPersist);
      window.removeEventListener('storage', onStorage);
    };
  }, [setSession]);

  const updatePlayerSlot = useCallback(
    (index, value) => {
      setSession((s) => {
        const slots = normalizePlayerSlots(s.players);
        slots[index] = value.slice(0, TABLE_NAME_MAX);
        return { ...s, players: slots };
      });
    },
    [setSession]
  );

  const addPlayerSlot = useCallback(() => {
    setSession((s) => {
      const slots = normalizePlayerSlots(s.players);
      if (slots.length >= MAX_TABLE_PLAYERS) return s;
      return { ...s, players: [...slots, ''] };
    });
  }, [setSession]);

  const removePlayerSlot = useCallback(
    (index) => {
      setSession((s) => {
        const slots = normalizePlayerSlots(s.players);
        if (slots.length <= 2) return s;
        return { ...s, players: slots.filter((_, i) => i !== index) };
      });
    },
    [setSession]
  );

  const [, setTick] = useState(0);
  const refresh = useCallback(() => setTick((x) => x + 1), []);

  useEffect(() => {
    const id = window.setInterval(refresh, 500);
    const onPersist = () => refresh();
    window.addEventListener('mt-persist', onPersist);
    return () => {
      window.clearInterval(id);
      window.removeEventListener('mt-persist', onPersist);
    };
  }, [refresh]);

  const rounds = readJson(STORAGE.rounds, { round: 1 });
  const dice = readJson(STORAGE.dice, {});
  const fp = readJson(STORAGE.firstPlayer, {});

  const lr = dice.lastRoll;
  const rolls = diceRollValues(lr);
  const total = diceLastTotal(dice);
  const dicePrev =
    total != null && rolls?.length
      ? `Last: ${rolls.join('+')}${lr?.modifier ? ` + ${lr.modifier}` : ''}`
      : total != null
        ? `Last: ${total}`
        : 'Tap to roll';

  const fpPrev = fp.result ? `Last: ${fp.result}` : 'Pick who starts';

  const filledNames = playerSlots.map((n) => String(n).trim()).filter(Boolean);
  const rosterPreview = filledNames.length > 0 ? filledNames.join(', ') : 'Add players below';

  return (
    <div className="hub-wrap">
      <header className="hub-header">
        <h1 className="hub-title">Game Night Kit</h1>
        <p className="hub-sub">
          {session.gameName.trim() ? (
            <>
              <span className="hub-session-game">{session.gameName.trim()}</span>
              <span className="hub-session-dot"> · </span>
            </>
          ) : null}
          Tonight&apos;s table tools
        </p>
      </header>

      <section className="hub-session" aria-labelledby="hub-session-heading">
        <h2 id="hub-session-heading" className="hub-session-heading">
          Table (saved everywhere)
        </h2>
        <label className="hub-session-label" htmlFor="hub-game-name">
          Game name
        </label>
        <input
          id="hub-game-name"
          className="hub-session-input"
          type="text"
          placeholder="e.g. Wingspan"
          maxLength={30}
          value={session.gameName}
          onChange={(e) => setSession((s) => ({ ...s, gameName: e.target.value }))}
          autoComplete="off"
        />
        <div className="hub-session-label-row">
          <label className="hub-session-label hub-session-label--inline" htmlFor="hub-player-0">
            Players
          </label>
          <span className="hub-session-sublabel">One row each · meeple style soon</span>
        </div>
        <ul className="hub-player-list">
          {playerSlots.map((name, index) => (
            <li key={index} className="hub-player-row">
              <span className="hub-player-meeple" aria-hidden title={`Seat ${index + 1}`}>
                {index + 1}
              </span>
              <input
                id={index === 0 ? 'hub-player-0' : undefined}
                className="hub-session-input hub-player-input"
                type="text"
                placeholder={`Player ${index + 1}`}
                maxLength={TABLE_NAME_MAX}
                value={name}
                onChange={(e) => updatePlayerSlot(index, e.target.value)}
                aria-label={`Player ${index + 1} name`}
                autoComplete="off"
              />
              {playerSlots.length > 2 ? (
                <button
                  type="button"
                  className="hub-player-remove"
                  onClick={() => removePlayerSlot(index)}
                  aria-label={`Remove player ${index + 1}`}
                >
                  ✕
                </button>
              ) : (
                <span className="hub-player-remove-spacer" aria-hidden />
              )}
            </li>
          ))}
        </ul>
        {playerSlots.length < MAX_TABLE_PLAYERS ? (
          <button type="button" className="hub-player-add" onClick={addPlayerSlot}>
            + Add player
          </button>
        ) : null}
        <p className="hub-session-hint">Saved automatically · syncs to Scorecard setup and Kit imports.</p>
      </section>

      <div className="hub-grid-wide">
        <aside className="hub-rail" aria-label="Quick links">
          <Link to="/scorecard">SC</Link>
          <Link to="/first-player">1st</Link>
          <Link to="/dice">🎲</Link>
          <Link to="/timer">⏱</Link>
          <Link to="/rounds">R</Link>
        </aside>
        <div className="hub-main-tiles">
          <KitTile
            to="/scorecard"
            title="Scorecard"
            subtitle="Track points"
            preview={session.gameName.trim() ? `${session.gameName.trim()} · ${rosterPreview}` : rosterPreview}
            accent="purple"
            className="hub-score-tile"
          />
          <div className="hub-tools-grid">
            <KitTile to="/first-player" title="1st player" subtitle="Who goes first?" preview={fpPrev} accent="gold" />
            <KitTile to="/dice" title="Dice" subtitle="Roll & history" preview={dicePrev} accent="cyan" />
            <KitTile to="/timer" title="Timer" subtitle="Turn / break" preview={timerPreview()} accent="cyan" />
            <KitTile to="/rounds" title="Round" subtitle="Round & phase" preview={`R${rounds.round ?? 1}`} accent="pink" />
          </div>
        </div>
      </div>

      <div className="hub-footer-note">
        Made for game nights · suggested tag{' '}
        <button
          type="button"
          onClick={() => copyText('#MeepleTabNight').then(() => alert('Copied #MeepleTabNight'))}
        >
          Copy #MeepleTabNight
        </button>
      </div>
    </div>
  );
}
